/**
 * Right-click context menu for map events.
 *
 * Shows a floating menu with actions: fly to, show detail, add to monitor.
 * Positioned at cursor, auto-dismisses on outside click or Escape.
 */

import { eventBus } from '@/core/events';
import { panelManager } from '@/panels/panel-manager';
import type { NormalizedEvent } from '@/sources/base-source';

interface MenuItem {
  label: string;
  icon: string;
  action: (event: NormalizedEvent) => void;
}

const MENU_ITEMS: MenuItem[] = [
  {
    label: 'Show detail',
    icon: '\uD83D\uDD0D',
    action: (event) => {
      eventBus.emit('event:selected', event);
      panelManager.showPanel('detail');
    },
  },
  {
    label: 'Fly to location',
    icon: '\uD83C\uDFAF',
    action: (event) => {
      if (event.location) {
        eventBus.emit('map:viewChanged', {
          center: [event.location.lng, event.location.lat],
          zoom: 8,
        });
      }
    },
  },
  {
    label: 'Add to monitor',
    icon: '\uD83D\uDD14',
    action: (event) => {
      panelManager.showPanel('monitor');
      // The monitor panel will pick up the event context
      eventBus.emit('event:selected', event);
    },
  },
];

let activeMenu: HTMLElement | null = null;
let outsideClickHandler: ((e: MouseEvent) => void) | null = null;
let escapeHandler: ((e: KeyboardEvent) => void) | null = null;

function dismiss(): void {
  if (activeMenu) {
    activeMenu.remove();
    activeMenu = null;
  }
  if (outsideClickHandler) {
    document.removeEventListener('mousedown', outsideClickHandler);
    outsideClickHandler = null;
  }
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler);
    escapeHandler = null;
  }
}

function show(x: number, y: number, event: NormalizedEvent): void {
  dismiss();

  const menu = document.createElement('div');
  menu.className = 'context-menu';
  menu.setAttribute('role', 'menu');

  // Title header
  const header = document.createElement('div');
  header.className = 'context-menu-header';
  const title = event.title ?? 'Event';
  header.textContent = title.length > 40 ? title.slice(0, 40) + '\u2026' : title;
  menu.appendChild(header);

  // Separator
  const sep = document.createElement('div');
  sep.className = 'context-menu-sep';
  menu.appendChild(sep);

  // Menu items
  for (const item of MENU_ITEMS) {
    // Skip "Fly to location" if no coordinates
    if (item.label === 'Fly to location' && !event.location) continue;

    const btn = document.createElement('button');
    btn.className = 'context-menu-item';
    btn.setAttribute('role', 'menuitem');

    const icon = document.createElement('span');
    icon.className = 'context-menu-icon';
    icon.textContent = item.icon;
    btn.appendChild(icon);

    const label = document.createElement('span');
    label.textContent = item.label;
    btn.appendChild(label);

    btn.onclick = () => {
      item.action(event);
      dismiss();
    };

    menu.appendChild(btn);
  }

  // Position — keep within viewport
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuWidth = 200;
  const menuHeight = MENU_ITEMS.length * 32 + 60; // approximate

  const left = x + menuWidth > vw ? vw - menuWidth - 8 : x;
  const top = y + menuHeight > vh ? vh - menuHeight - 8 : y;

  menu.style.cssText = `
    position: fixed;
    left: ${left}px;
    top: ${top}px;
    z-index: 10001;
  `;

  document.body.appendChild(menu);
  activeMenu = menu;

  // Dismiss on outside click (after current event loop)
  setTimeout(() => {
    outsideClickHandler = (e: MouseEvent) => {
      if (activeMenu && !activeMenu.contains(e.target as Node)) {
        dismiss();
      }
    };
    document.addEventListener('mousedown', outsideClickHandler);
  }, 0);

  // Dismiss on Escape
  escapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') dismiss();
  };
  document.addEventListener('keydown', escapeHandler);
}

const cleanupFns: (() => void)[] = [];

/** Initialize the context menu system. Returns cleanup function. */
export function initContextMenu(): () => void {
  destroyContextMenu();

  // The map interaction layer should emit a custom event for right-clicks.
  // We listen for it here. For now, expose show() as a callable API and
  // also listen on the map container for contextmenu events.
  const mapContainer = document.getElementById('map-container');
  if (mapContainer) {
    const handler = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      // Only show if there's a selected event or we can detect one
      // For now, this is wired via the event bus approach below
      mouseEvent.preventDefault();
    };
    mapContainer.addEventListener('contextmenu', handler);
    cleanupFns.push(() => mapContainer.removeEventListener('contextmenu', handler));
  }

  return destroyContextMenu;
}

/** Show context menu for a specific event at given coordinates. */
export function showContextMenu(x: number, y: number, event: NormalizedEvent): void {
  show(x, y, event);
}

/** Clean up context menu. */
export function destroyContextMenu(): void {
  dismiss();
  for (const fn of cleanupFns) fn();
  cleanupFns.length = 0;
}
